import { useDisclosure } from '@mantine/hooks';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { SegmentExport } from '@feathr/blackbox';
import { toast } from '@feathr/components';

export interface IExportRowActionsModal {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export interface IExportRowActionsHook {
  stopModal: IExportRowActionsModal;
  archiveModal: IExportRowActionsModal;
  onConfirmStop: () => Promise<void>;
  onConfirmArchive: () => Promise<void>;
  onDownload: () => void;
}

interface IUseExportRowActionsOptions {
  /** Override for tests; defaults to `window.open`. */
  openWindow?: (url: string, target: string, features: string) => void;
}

/**
 * Encapsulates row-action state + handlers for a single SegmentExport row.
 *
 * - Stop: PATCH `{state: 'stopped'}` (BE rejects with 409 if already terminal).
 *   The user-facing label is "Stop" per Figma; the BE state remains `stopped`.
 * - Archive: PATCH `{is_archived: true}` via rachis `model.archive()`. The row
 *   is removed from the collection only after the PATCH succeeds; on failure the
 *   row stays and we surface an error toast.
 * - Download: opens the BE-hosted file-listing page in a new tab. That page may
 *   404 until its BE endpoint is deployed.
 *
 * @param record  The SegmentExport row whose actions are being wired.
 * @param options Optional overrides (e.g. injecting a test double for window.open).
 */
export function useExportRowActions(
  record: SegmentExport,
  options: IUseExportRowActionsOptions = {},
): IExportRowActionsHook {
  const { t } = useTranslation();
  const { openWindow = openInNewTab } = options;

  const [stopIsOpen, { open: openStop, close: closeStop }] = useDisclosure(false);
  const [archiveIsOpen, { open: openArchive, close: closeArchive }] = useDisclosure(false);

  async function onConfirmStop(): Promise<void> {
    try {
      await record.patch({ state: 'stopped' });
      /*
       * rachis `model.patch` resolves (setting `isErrored`) on an HTTP error
       * rather than rejecting, so a server rejection (e.g. 409 stopping an
       * already-terminal export) lands here, not in `catch`. Surface it as a
       * failure explicitly.
       */
      if (record.isErrored) {
        throw record.error;
      }
      toast(t('Export cancelled.'), { type: 'success' });
    } catch {
      toast(t("Couldn't cancel export. Try again."), { type: 'error' });
    } finally {
      closeStop();
    }
  }

  async function onConfirmArchive(): Promise<void> {
    try {
      await record.archive();
      toast(t('Export archived.'), { type: 'success' });
    } catch {
      toast(t("Couldn't archive export. Try again."), { type: 'error' });
    } finally {
      closeArchive();
    }
  }

  function onDownload(): void {
    const url = record.downloadUrl;
    if (!url) {
      return;
    }
    openWindow(url, '_blank', 'noopener,noreferrer');
  }

  return {
    stopModal: { isOpen: stopIsOpen, open: openStop, close: closeStop },
    archiveModal: { isOpen: archiveIsOpen, open: openArchive, close: closeArchive },
    onConfirmStop,
    onConfirmArchive,
    onDownload,
  };
}

function openInNewTab(url: string, target: string, features: string): void {
  globalThis.open(url, target, features);
}

export interface IConfirmCopy {
  title: string;
  alertTitle: string;
  alertDescription: string;
  confirmButtonText: string;
}

export function getStopConfirmCopy(record: SegmentExport, t: TFunction): IConfirmCopy {
  const name = record.name || t('this export');
  return {
    title: t('Stop "{{- name}}"', { name }),
    alertTitle: t('Are you sure you want to stop this export?'),
    alertDescription: t(
      "Stopping will end the export immediately. Any files already produced won't be available.",
    ),
    confirmButtonText: t('Stop export'),
  };
}

export function getArchiveConfirmCopy(record: SegmentExport, t: TFunction): IConfirmCopy {
  const name = record.name || t('this export');
  return {
    title: t('Archive "{{- name}}"', { name }),
    alertTitle: t('Are you sure you want to archive this export?'),
    alertDescription: t(
      "Archiving will permanently remove this export from your list. This can't be undone.",
    ),
    confirmButtonText: t('Archive'),
  };
}
